import { getPublicVoteCookie, getCurrentLanguageId, getToken } from "../utils";
import {
  migrateKurdishTalentPublicAuth,
  KT_AUTH_TOKEN_KEY,
} from "./authStorageKeys";
import { normalizeLoginEmail } from "./loginFormUtils";
import { app } from "../services/third-party/firebase";
import {
  getStorage,
  ref,
  deleteObject,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";
import uuid from "react-uuid";

// export const ApiBase = "http://127.0.0.1:8000/";
export const ApiBase =
  process.env.REACT_APP_API_BASE ||
  "https://kurdishtalent-api-48516816480.europe-north2.run.app/";
export const SiteBase =
  process.env.REACT_APP_SITE_BASE || "https://kurdishtalent.com";

const MediaBase =
  `${String(ApiBase).replace(/\/$/, "").replace(/^http:/i, "https:")}/api/uploaded_media`;

/** Absolute URL for uploaded media (served by API → GCS proxy in production). */
export function resolveMediaUrl(path) {
  if (path == null || path === "") return "";
  let s = String(path).trim();
  const apiBase = String(ApiBase).replace(/\/$/, "").replace(/^http:/i, "https:");

  // Unwrap absolute URLs wrongly nested under the media proxy
  // e.g. /api/uploaded_media/https://lh3.googleusercontent.com/...
  const nestedAbs = s.match(
    /(?:https?:\/\/[^/]+)?\/api\/uploaded_media\/(https?:\/\/.+)$/i
  );
  if (nestedAbs) {
    return nestedAbs[1];
  }

  // Rewrite private GCS object URLs to the API media proxy.
  const gcsMatch = s.match(
    /^https?:\/\/storage\.googleapis\.com\/[^/]+\/(?:uploaded_media\/extracted_media\/uploaded_media\/)?(.+)$/i
  );
  if (gcsMatch) {
    return `${apiBase}/api/uploaded_media/${gcsMatch[1]}`;
  }

  if (/^https?:\/\//i.test(s)) {
    const apiMediaMatch = s.match(/^https?:\/\/[^/]+\/api\/uploaded_media\/(.+)$/i);
    if (apiMediaMatch) {
      const inner = apiMediaMatch[1];
      if (/^https?:\/\//i.test(inner)) {
        return inner;
      }
      return `${apiBase}/api/uploaded_media/${inner}`;
    }
    if (/^http:\/\//i.test(s) && s.includes(".run.app")) {
      return s.replace(/^http:\/\//i, "https://");
    }
    return s;
  }

  if (s.startsWith("/api/uploaded_media/")) {
    return `${apiBase}${s}`;
  }
  if (s.startsWith("api/uploaded_media/")) {
    return `${apiBase}/${s}`;
  }
  if (s.startsWith("/")) {
    return `${apiBase}${s}`;
  }
  return `${MediaBase}/${s}`;
}

migrateKurdishTalentPublicAuth();

/** Same key as main app session; exported for services/http.js */
export const TOKEN_KEY = KT_AUTH_TOKEN_KEY;

export function createJsonHeaders(includeAuth = true) {
  migrateKurdishTalentPublicAuth();
  const h = new Headers();
  h.append("Content-Type", "application/json");
  h.append("Accept", "application/json");
  if (includeAuth) {
    const t = localStorage.getItem(KT_AUTH_TOKEN_KEY);
    if (t) h.append("Authorization", `Bearer ${t}`);
  }
  return h;
}

export function createFileHeaders(includeAuth = true) {
  migrateKurdishTalentPublicAuth();
  const h = new Headers();
  h.append("Accept", "application/json");
  if (includeAuth) {
    const t = localStorage.getItem(KT_AUTH_TOKEN_KEY);
    if (t) h.append("Authorization", `Bearer ${t}`);
  }
  return h;
}

export async function loadTalents(language_id) {
  const response = await fetch(
    `${ApiBase}api/talent/?language_id=${language_id}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}

export async function loadTranslations() {

  const response = await fetch(
    `${ApiBase}api/translate/?size=100000`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) {
    return data.results;
  }

}

export async function loadMenuTalents() {
  let language_id = getCurrentLanguageId();

  const response = await fetch(
    `${ApiBase}api/talent/?menu=1&language_id=${language_id}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}

export async function loadTalent(id) {
  const response = await fetch(`${ApiBase}api/talent/${id}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadTalentDocument(talent_id) {
  const response = await fetch(`${ApiBase}api/talent/${talent_id}/documents`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results[0];
  }
}

export async function loadUserTalents(query = {}) {
  const search = new URLSearchParams();
  if (query.size != null) search.set("size", String(query.size));
  if (query.page != null) search.set("page", String(query.page));
  const qs = search.toString();
  const response = await fetch(
    `${ApiBase}api/usertalent/${qs ? `?${qs}` : ""}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) {
    return data.results;
  } else return data;
}

/** Academy course cards for the logged-in user (from their UserTalent talents only). */
export async function loadMyAcademyCourses() {
  const response = await fetch(
    `${ApiBase}api/usertalent/academy-courses/`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );
  const data = await response.json();
  return Array.isArray(data) ? data : [];
}

export async function loadUserTalentDocuments(usertalent_id,talent_id) {

  let response;
  if(usertalent_id){
    response = await fetch(
      `${ApiBase}api/usertalent/${usertalent_id}/userdocuments/`,
      {
        headers: createJsonHeaders(),
        method: "GET",
      }
    );
  }
  else if(talent_id){
    response = await fetch(
      `${ApiBase}api/talent/${talent_id}/userdocuments/`,
      {
        headers: createJsonHeaders(),
        method: "GET",
      }
    );
  }
  else {
    return [];
  }

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    return [];
  }

  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}


export async function loadUserDocuments() {

  let response;
  response = await fetch(
    `${ApiBase}api/userdocuments/`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );
  

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

const PROFILE_CACHE_KEY = "kt_participant_profile_v1";

/** Last loaded participant profile (session) — keeps header widgets from flashing on route change. */
export function getCachedProfile() {
  try {
    const raw = sessionStorage.getItem(PROFILE_CACHE_KEY);
    if (!raw) return null;
    const profile = JSON.parse(raw);
    return profile && profile.id ? profile : null;
  } catch (_) {
    return null;
  }
}

export function setCachedProfile(profile) {
  try {
    if (profile && profile.id) {
      sessionStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    }
  } catch (_) {
    /* ignore quota / private mode */
  }
}

export function clearCachedProfile() {
  try {
    sessionStorage.removeItem(PROFILE_CACHE_KEY);
  } catch (_) {
    /* ignore */
  }
}

export async function loadProfile() {
  if (getToken()) {
    const response = await fetch(`${ApiBase}auth/profiles/`, {
      headers: createJsonHeaders(),
      method: "GET",
    });

    const data = await response.json();

    if (data.results) {
      const profile = data.results[0];
      setCachedProfile(profile);
      return profile;
    }
  }
}

export async function loadCourse(id) {
  const response = await fetch(`${ApiBase}api/course/${id}/`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadCourses(query = {}) {
  const search = new URLSearchParams();
  if (query.size != null) search.set("size", String(query.size));
  if (query.page != null) search.set("page", String(query.page));
  const qs = search.toString();
  const response = await fetch(
    `${ApiBase}api/course/${qs ? `?${qs}` : ""}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) return data.results;
}

export async function loadSupportMessages() {
  const response = await fetch(`${ApiBase}api/support/`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) return data.results;
}

export async function loadSupportMessage(id) {
  const response = await fetch(`${ApiBase}api/supportmessages/${id}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadMessageReplies(message_id, page=1) {
  var filter_query = "";
  if (page) filter_query = "?page=" + page;

  const response = await fetch(`${ApiBase}api/supportmessages/${message_id}/replies${filter_query}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function saveReply(supportmessage_id, message) {
  let body = {
    message: message,
  };

  const response = await fetch(`${ApiBase}api/supportmessages/${supportmessage_id}/replies`, {
    headers: createJsonHeaders(),
    method: 'POST',
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function loadNewestCourse(talent_id,loadall=false) {
  const response = await fetch(`${ApiBase}api/talent/${talent_id}/courses?${loadall ? "status=all" : ""}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) return data.results[0];
}

export async function checkDocumentAvailability(url) {
  const result = await fetch(url, { method: 'HEAD' });
  return result.ok;
}



export async function addParticipantToCourse(
  course_id,
  usertalent_id,
  userdocument_ids
) {
  let body = {
    course: course_id,
    usertalent: usertalent_id,
    userdocument_ids: userdocument_ids,
  };
  console.log(body);
  const response = await fetch(`${ApiBase}api/courseparticipant/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function loadCourseParticipants(course_id) {
  const response = await fetch(
    `${ApiBase}api/courseparticipant?course=${course_id}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    return [];
  }

  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}
export async function loadCourseDocuments(course, rating) {
  var query_text = "?size=300";
  if (course) query_text += "&course=" + course;
  if (rating != null && rating >= 0) query_text += "&is_rated=" + rating;

  const response = await fetch(`${ApiBase}api/coursedocument/${query_text}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    return { results: [], count: 0 };
  }

  if (data && Array.isArray(data.results)) {
    return data;
  }
  if (Array.isArray(data)) {
    return { results: data, count: data.length };
  }
  return { results: [], count: 0 };
}

export async function loadPublicCourseDocuments(params) {
  var query_text = "?size=30";
  if (params.size) query_text = "?size=" + params.size;
  if (params.page) query_text += "&page=" + params.page;
  if (params.course) query_text += "&course=" + params.course;
  if (params.rating) query_text += "&rating=" + params.rating;
  if (params.talent) query_text += "&talent=" + params.talent;

  const response = await fetch(
    `${ApiBase}api/publiccoursedocuments/${query_text}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
}

export async function loadCourseDocument(course) {
  const response = await fetch(
    `${ApiBase}api/coursedocument/?course=${course}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) {
    return data.results[0];
  }
}
export async function AddCourseDocument(course_id, userdocument_id) {
  let body = {
    course: course_id,
  };
  const response = await fetch(
    `${ApiBase}api/userdocument/${userdocument_id}/coursedocuments/`,
    {
      headers: createJsonHeaders(),
      method: "POST",
      body: JSON.stringify(body),
    }
  );

  const data = await response.json();

  return data;
}

export async function loadQuestions(talent_id) {
  let language_id = getCurrentLanguageId();
  const response = await fetch(
    `${ApiBase}api/talent/${talent_id}/questions/?language_id=${language_id}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  let data = null;
  try {
    data = await response.json();
  } catch (e) {
    return [];
  }

  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.results)) return data.results;
  return [];
}
export async function loadDegrees(course, participant, talent, referee) {

  const response = await fetch(
    `${ApiBase}rep/refereedegreesummary?course=${course}&participant=${participant}&talent=${talent}&referee=${referee}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (!data.error) return data;
}

export async function loadRefereeDashboardStats() {
  const response = await fetch(`${ApiBase}rep/refereedashboardstats`, {
    headers: createJsonHeaders(),
    method: "GET",
  });
  if (response.status === 401 || response.status === 403) {
    return null;
  }
  return response.json();
}

export async function loadPublicDegree(course, usertalent) {
  var cookie = getPublicVoteCookie()

  const response = await fetch(
    `${ApiBase}api/peopledegree?course=${course}&usertalent=${usertalent}&c=${cookie}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) return data.results[0];
}
export async function loadSettings() {
  const response = await fetch(`${ApiBase}api/settings/`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) return data.results[0];
}

export async function loadTestimonials(language) {
  const response = await fetch(
    `${ApiBase}api/testimonials/?language=${language}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if (data.results) return data.results;
}
export async function loadPosts(talent, size = 10, page = 1,featured=null) {
  let language_id = getCurrentLanguageId();

  var query_text = "?size=" + size;
  if (page) query_text += "&page=" + page;
  if (talent) query_text += "&talent=" + talent;
  if (language_id) query_text += "&language=" + language_id;
  if (featured!=null) query_text += "&featured=" + featured;

  const response = await fetch(`${ApiBase}postapi/post/${query_text}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();


  if (data.results) return data.results;
}

export async function loadFaqs(size = 100, page = 1) {
  // let language_id = getCurrentLanguageId();


  var query_text = "?size=" + size;
  if (page) query_text += "&page=" + page;
  // if (language_id) query_text += "&language=" + language_id;

  const response = await fetch(`${ApiBase}api/faq${query_text}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) return data.results;
}

export async function loadPostsCount(talent) {
  let query_text = "?size=100000";
  if (talent) query_text += "&talent=" + talent;

  const response = await fetch(`${ApiBase}postapi/post/${query_text}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.count) return data.count;
}
export async function loadPost(id) {
  const response = await fetch(`${ApiBase}postapi/post/${id}/`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}

export async function loadCountries(page, size = 10, language_id) {
  var query_text = "?size=" + size;
  if (page) query_text += "&page=" + page;
  //if (language_id)
  //    query_text += "&language_id=" + language_id;

  const response = await fetch(`${ApiBase}base/country${query_text}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    var coutries = data.results;
    return data.results;
  }
}
export async function loadCountry(country_id) {
  const response = await fetch(`${ApiBase}base/country/${country_id}/`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function loadCities(country_id) {
  const response = await fetch(
    `${ApiBase}base/country/${country_id}/cities?size=1000000`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}
export async function loadCity(id) {
  const response = await fetch(`${ApiBase}base/city/${id}/`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  return data;
}
export async function saveUserTalent(talent_id) {
  let body = {
    talent: talent_id,
  };
  const response = await fetch(`${ApiBase}api/usertalent/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function saveNewsletter(email) {
  let body = {
    email: email,
  };
  const response = await fetch(`${ApiBase}api/newsletter/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function saveSupportMessage(title,message) {
  let body = {
    title: title,
    message: message,
  };
  const response = await fetch(`${ApiBase}api/support/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function saveQuestionDegree(
  degree_id,
  course,
  question,
  degree,
  participant,
  talent,
  referee
) {
  if (degree_id)
    updateDegree(
      degree_id,
      course,
      question,
      degree,
      participant,
      talent,
      referee
    );
  else insertDegree(course, question, degree, participant, talent);
}

export async function savePublicDegree(degree_id, degree, course, usertalent,cookie) {
  if (degree_id) updatePublicDegree(degree_id, degree, course, usertalent);
  else insertPublicDegree(degree, course, usertalent,cookie);
}

async function insertPublicDegree(degree, course, usertalent,cookie) {
  let body = {
    course: course,
    degree: degree ? degree : 5,
    usertalent: usertalent,
    cookie: cookie,
  };
  const response = await fetch(`${ApiBase}api/peopledegree/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

async function updatePublicDegree(degree_id, degree) {
  if (!degree) return;

  let body = {
    degree: degree,
  };

  const response = await fetch(`${ApiBase}api/peopledegree/${degree_id}`, {
    headers: createJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

async function updateDegree(
  degree_id,
  course,
  question,
  degree,
  participant,
  talent,
  referee
) {
  //console.log(degree, degree_id)

  if (!degree) return;

  let body = {
    degree: degree,
  };

  const response = await fetch(`${ApiBase}api/degree/${degree_id}`, {
    headers: createJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

async function insertDegree(course, question, degree, participant, talent) {
  let body = {
    course: course,
    degree: degree ? degree : 3,
    question: question,
    participant: participant,
    talent: talent,
  };

  const response = await fetch(`${ApiBase}api/degree/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function resendVerificationEmail(email) {
  let body = {
    email: email,
    redirect_url: `${SiteBase}/validate/`,
  };
  const response = await fetch(`${ApiBase}auth/resendverificationemail/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function requestReset(email) {
  let body = {
    email: email,
    redirect_url: `${SiteBase}/reset/`,
  };
  const response = await fetch(`${ApiBase}auth/request-reset-email/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function completeReset(token, uidb64, password) {
  let body = {
    token: token,
    uidb64: uidb64,
    password: password,
  };
  const response = await fetch(`${ApiBase}auth/password-reset-complete`, {
    headers: createJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}
export async function checkResetToken(token, uidb64) {
  const response = await fetch(
    `${ApiBase}auth/password-reset/${uidb64}/${token}/`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data;
}

export async function uploadProfileImage(profile_id, file) {
  const formData = new FormData();

  formData.append("image", file, file.name);
  const response = await fetch(`${ApiBase}auth/profiles/${profile_id}/`, {
    headers: createFileHeaders(),
    method: "PATCH",
    body: formData,
  });

  const data = await response.json();

  return data;
}

function parseApiErrorDetail(body) {
  const d = body && (body.detail ?? body.error ?? body.Error);
  if (d == null) return null;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) {
    return d
      .map((item) =>
        typeof item === "string"
          ? item
          : item &&
            (item.string || item.msg || item.message || JSON.stringify(item))
      )
      .filter(Boolean)
      .join(" ");
  }
  if (typeof d === "object")
    return d.string || d.msg || d.message || JSON.stringify(d);
  return String(d);
}

/**
 * Upload a talent video to Firebase Storage (GCP bucket kurdishtalent-bfa78).
 * Resolves to { ok: true, public_url } or { ok: false, error }.
 * @param {File} file
 * @param {(pct: number) => void} [onProgress] 0–100
 */
export function uploadTalentVideoToFirebase(file, onProgress) {
  return new Promise((resolve) => {
    try {
      const ext = (file?.name && String(file.name).split(".").pop()) || "mp4";
      const objectPath = `videos/${uuid()}.${ext}`;
      const storage = getStorage(app);
      const storageRef = ref(storage, objectPath);
      const task = uploadBytesResumable(storageRef, file, {
        contentType: file?.type || "video/mp4",
      });
      task.on(
        "state_changed",
        (snapshot) => {
          if (typeof onProgress === "function" && snapshot.totalBytes > 0) {
            onProgress(
              Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
            );
          }
        },
        (error) => {
          resolve({
            ok: false,
            error: (error && error.message) || "Video upload failed",
          });
        },
        async () => {
          try {
            const public_url = await getDownloadURL(task.snapshot.ref);
            if (typeof onProgress === "function") onProgress(100);
            resolve({ ok: true, public_url });
          } catch (e) {
            resolve({
              ok: false,
              error: (e && e.message) || "Could not get download URL",
            });
          }
        }
      );
    } catch (e) {
      resolve({
        ok: false,
        error: (e && e.message) || "Video upload failed",
      });
    }
  });
}

export async function uploadUsertalentDocument(
  usertalent_id,
  document_id,
  file,
  url,
  id,
  talent_id
) {
  let formData = new FormData();
  formData.append("document", document_id);
  formData.append("url", url);

  console.log(document_id)


  if (file) formData.append("file", file, file.name);
  var response;

  if(id){
    response = await fetch(
      `${ApiBase}api/userdocument/${id}`,
      {
        headers: createFileHeaders(),
        method: "PUT",
        body: formData,
      }
    ).catch((error) => {
      console.log(error);
    });
  }
  else {
    if(usertalent_id){
      response = await fetch(
        `${ApiBase}api/usertalent/${usertalent_id}/userdocuments/`,
        {
          headers: createFileHeaders(),
          method: "POST",
          body: formData,
        }
      ).catch((error) => {
        console.log(error);
      });
    }
    else{ // Upload document when there is no usertalent_id
      response = await fetch(
        `${ApiBase}api/talent/${talent_id}/userdocuments/`,
        {
          headers: createFileHeaders(),
          method: "POST",
          body: formData,
        }
      ).catch((error) => {
        console.log(error);
      });
    }
    
  }
  

  const data = await response.json();

  return data;
}
export async function loadUserTalentCertificates(userttalent_id) {
  const response = await fetch(
    `${ApiBase}api/usertalent/${userttalent_id}/certs/`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data.results;
}
export async function loadCertificates() {
  const response = await fetch(
    `${ApiBase}api/certificates`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  return data.results;
}
export async function uploadCertificate(title, file) {
  let formData = new FormData();
  formData.append("title", title);
  formData.append("file", file, file.name);
  const response = await fetch(
    `${ApiBase}api/certificates`,
    {
      headers: createFileHeaders(),
      method: "POST",
      body: formData,
    }
  ).catch((error) => {
    console.log(error);
  });

  const data = await response.json();

  //console.log(response.body);

  return data;
}




export async function updateUserDocument(userdocument_id, document_id, file) {
  let formData = new FormData();
  formData.append("document", document_id);
  formData.append("file", file, file.name);
  const response = await fetch(
    `${ApiBase}api/userdocument/${userdocument_id}`,
    {
      headers: createFileHeaders(),
      method: "PUT",
      body: formData,
    }
  );

  const data = await response.json();

  return data;
}

export async function deleteUserTalent(usertalent_id) {
  const response = await fetch(`${ApiBase}api/usertalent/${usertalent_id}`, {
    headers: createJsonHeaders(),
    method: "DELETE",
  });
  if (response.ok) {
    return;
  }
  let detail = response.statusText || `Request failed (${response.status})`;
  try {
    const body = await response.json();
    console.log(body);
    detail = parseApiErrorDetail(body) || detail;
  } catch (_) {
    /* non-JSON or empty body */
  }
  const err = new Error(detail);
  err.status = response.status;
  throw err;
}

export async function deleteCourseParticipant(courseparticipant_id) {
  const response = await fetch(
    `${ApiBase}api/courseparticipant/${courseparticipant_id}`,
    {
      headers: createJsonHeaders(),
      method: "DELETE",
    }
  );

  if (!response.ok) {
    let detail = response.statusText || `Request failed (${response.status})`;
    try {
      const body = await response.json();
      detail = parseApiErrorDetail(body) || detail;
    } catch (_) {
      /* non-JSON or empty body */
    }
    const err = new Error(detail);
    err.status = response.status;
    throw err;
  }

  return response;
}
export async function loadCourseReferees(size = 200) {
  const response = await fetch(`${ApiBase}api/coursereferee/?size=${size}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) {
    return data.results;
  }
  return [];
}

export async function deleteUserTalentCertificate(certificate_id) {
  const response = await fetch(`${ApiBase}api/cert/${certificate_id}`, {
    headers: createJsonHeaders(),
    method: "DELETE",
  });
}

const getfilename = (url)=>{
  return url ;
}


export async function deleteUserTalentDocument(userdocument) {
  const response = await fetch(
    `${ApiBase}api/userdocument/${userdocument.id}`,
    {
      headers: createJsonHeaders(),
      method: "DELETE",
    }
  );

  if (!response.ok) {
    let detail = response.statusText || "Unable to delete this document. Please try again.";
    try {
      const body = await response.json();
      detail = parseApiErrorDetail(body) || detail;
      if (/assigned to a CourseDocument/i.test(detail)) {
        detail = "This video is being used in a contest. Unroll before deleting it.";
      }
    } catch (_) {
      /* non-JSON or empty body */
    }
    const err = new Error(detail);
    err.status = response.status;
    throw err;
  }

  if (userdocument.url?.includes("firebasestorage")) {
    const storage = getStorage(app);
    const documentRef = ref(storage, userdocument.url);
    deleteObject(documentRef).catch(() => {});
  }

  return response;
}

export async function saveProfile(user) {
  let body = {
    first_name: user.first_name,
    last_name: user.last_name,
    gender: user.gender,
    phone_number: user.phone_number ? user.phone_number : "",
    birth_date: user.birth_date,
    country_id: user.country_id,
    city_id: user.city_id,
    livingcountry_id: user.livingcountry_id,
    livingcity_id: user.livingcity_id,
    //username: user.username,
    // //password: user.password,
    website: user.website ? user.website : "",
    facebook: user.facebook ? user.facebook : "",
    instagram: user.instagram ? user.instagram : "",
    youtube: user.youtube ? user.youtube : "",
    snapchat: user.snapchat ? user.snapchat : "",
    spotify: user.spotify ? user.spotify : "",
    comments: user.comments ? user.comments : "",
  };

  const response = await fetch(`${ApiBase}auth/profiles/${user.id}/`, {
    headers: createJsonHeaders(),
    method: "PATCH",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function changePassword(user) {
  let body = {
    password: user.password,
    first_name: user.first_name,
    last_name: user.last_name,
  };

  const response = await fetch(`${ApiBase}auth/profiles/${user.id}/`, {
    headers: createJsonHeaders(),
    method: "PUT",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

/**
 * Deactivate the current user's account (requires password). Returns { ok, status, data }.
 */
export async function deactivateAccount(password) {
  const response = await fetch(`${ApiBase}auth/account/deactivate/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify({ password }),
  });
  let data = null;
  if (response.status !== 204) {
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }
  }
  return { ok: response.ok, status: response.status, data };
}

/**
 * Permanently delete the current user's account.
 * Confirm with `{ password }` or `{ email }` (must match the account).
 * Returns { ok, status, data }.
 */
export async function deleteAccount({ password, email } = {}) {
  const body = {};
  if (password) body.password = password;
  if (email) body.email = email;

  const response = await fetch(`${ApiBase}auth/account/delete/`, {
    headers: createJsonHeaders(),
    method: "POST",
    body: JSON.stringify(body),
  });
  let data = null;
  if (response.status !== 204) {
    try {
      data = await response.json();
    } catch (e) {
      data = {};
    }
  }
  return { ok: response.ok, status: response.status, data };
}

export async function doLogin(user) {
  let body = {
    email: normalizeLoginEmail(user.email),
    password: user.password,
  };

  const response = await fetch(`${ApiBase}auth/login/`, {
    headers: createJsonHeaders(false),
    method: "POST",
    body: JSON.stringify(body),
  });

  const data = await response.json();

  return data;
}

export async function loadPages(url) {
  let language_id = getCurrentLanguageId();

  var query_text = "?url=" + url;
  if (language_id) query_text += "&language=" + language_id;

  const response = await fetch(`${ApiBase}postapi/page${query_text}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) return data.results;
}


export async function loadVideoGalleries() {

  let language_id = getCurrentLanguageId();

  const response = await fetch(
    `${ApiBase}postapi/videogallery/?language=${language_id}`,
    {
      headers: createJsonHeaders(),
      method: "GET",
    }
  );

  const data = await response.json();

  if(data.results)
    return data.results;
}

export async function loadPopupMessages() {
  let language_id = getCurrentLanguageId();
  const response = await fetch(`${ApiBase}postapi/popupmessage/?language=${language_id}`, {
    headers: createJsonHeaders(),
    method: "GET",
  });

  const data = await response.json();

  if (data.results) return data.results;
}
